import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { FormObject, InterceptedFormsObject } from '../../../../../@types';
import { ActionPermissions, CustomPermission } from '../../../../../@types';
import { ServiceObject } from '../../../../../@types/service.type';
import { HttpMethod } from '../../../../../api/http-client';
import { isUserHasPermission } from '../../../../../configs/permissions';
import { FormInterceptorContext } from '../../../../auth/AxiosInterceptor';
import { useSubmitFormsDataWithOriginalPayload } from '../../../../../queries/interactions-query';
import { useGetPendingInteractionsByServiceId } from '../../../../../queries/pendingInteraction-query';
import { getSortedPendingInteractions } from '../../../../../utils/interactions';
import { InteractionFormCard } from './InteractionFormCard';
import { InteractionHeader } from './InteractionHeader';

export interface InteractionPanelProps {
  service: ServiceObject;
  serviceId: number;
  permissions?: CustomPermission[];
  onComplete?: () => void;
}

export const InteractionPanel: React.FC<InteractionPanelProps> = ({
  service,
  serviceId,
  permissions,
  onComplete,
}) => {
  const { interceptedFormsObject, setInterceptedFormsObject, setHasInterceptedForms } = useContext(FormInterceptorContext);
  const pendingQuery = useGetPendingInteractionsByServiceId(serviceId);
  const submitMutation = useSubmitFormsDataWithOriginalPayload(serviceId);

  const [formValues, setFormValues] = useState<FormObject[]>([]);
  const [pendingFormValues, setPendingFormValues] = useState<FormObject[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canBypass = isUserHasPermission(
    ActionPermissions.Configure_Interactions_Bypass_Mandatory,
    permissions,
  );

  const hasForms = (interceptedFormsObject?.forms?.length ?? 0) > 0;
  const hasPending = pendingFormValues.length > 0 || (pendingQuery.data?.length ?? 0) > 0;
  const isPendingActive = !hasForms && pendingFormValues.length > 0;
  const isVisible = hasForms || hasPending;

  // When intercepted forms arrive, populate formValues
  useEffect(() => {
    if (interceptedFormsObject?.forms?.length) {
      setFormValues(interceptedFormsObject.forms);
      setError('');
    }
  }, [interceptedFormsObject]);

  // When pending interactions load, populate pendingFormValues for editing
  useEffect(() => {
    if (pendingQuery.data?.length && !hasForms) {
      const withOrder = pendingQuery.data.filter((f) => f.displayOrder);
      const withoutOrder = pendingQuery.data.filter((f) => !f.displayOrder);
      const sorted = getSortedPendingInteractions(withOrder, withoutOrder);
      setPendingFormValues(sorted as unknown as FormObject[]);
    }
  }, [pendingQuery.data]);

  const mandatoryForms = useMemo(
    () => formValues.filter((f) => f.isMandatory),
    [formValues],
  );

  const optionalForms = useMemo(
    () => formValues.filter((f) => !f.isMandatory),
    [formValues],
  );

  // Validate mandatory fields have values
  const validateForms = useCallback((): boolean => {
    for (const form of mandatoryForms) {
      for (const container of form.containers ?? []) {
        for (const field of container.fields ?? []) {
          if (field.isRequired && (field.value == null || field.value === '')) {
            if (!canBypass) return false;
          }
        }
      }
    }
    return true;
  }, [mandatoryForms, canBypass]);

  const handleSubmit = useCallback(async () => {
    // Submit pending interactions when no intercepted forms
    if (isPendingActive) {
      setError('');
      setSubmitting(true);
      try {
        const pendingFormsObject: InterceptedFormsObject = {
          url: `/pendingInteractions/${serviceId}`,
          method: HttpMethod.Put,
          payload: '',
          forms: pendingFormValues,
        };
        await submitMutation.mutateAsync(pendingFormsObject);
        setPendingFormValues([]);
        onComplete?.();
      } catch {
        setError('Failed to submit pending interactions');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!interceptedFormsObject) return;

    if (!validateForms()) {
      setError('Please complete all mandatory fields before submitting');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const updatedFormsObject: InterceptedFormsObject = {
        ...interceptedFormsObject,
        forms: formValues,
      };
      await submitMutation.mutateAsync(updatedFormsObject);
      setInterceptedFormsObject(undefined);
      setHasInterceptedForms(false);
      setFormValues([]);
      onComplete?.();
    } catch {
      setError('Failed to submit interaction forms');
    } finally {
      setSubmitting(false);
    }
  }, [isPendingActive, interceptedFormsObject, formValues, pendingFormValues, serviceId, validateForms, submitMutation, setInterceptedFormsObject, setHasInterceptedForms, onComplete]);

  const handleSkip = useCallback(() => {
    if (!canBypass && mandatoryForms.length > 0) {
      setError('You do not have permission to skip mandatory interactions');
      return;
    }
    setInterceptedFormsObject(undefined);
    setHasInterceptedForms(false);
    setFormValues([]);
    setPendingFormValues([]);
  }, [canBypass, mandatoryForms, setInterceptedFormsObject, setHasInterceptedForms]);

  const handleFormChange = useCallback((updatedForms: FormObject[]) => {
    setFormValues(updatedForms);
    setError('');
  }, []);

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        mx: 2.5,
        mb: 1.5,
        border: '2px solid',
        borderColor: mandatoryForms.length > 0 ? 'warning.main' : 'v4.border',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <InteractionHeader
        mandatoryCount={mandatoryForms.length}
        optionalCount={optionalForms.length}
        pendingCount={pendingQuery.data?.length ?? 0}
        hasForms={hasForms}
        canBypass={canBypass}
        submitting={submitting}
        onSkip={handleSkip}
        onSubmit={handleSubmit}
      />

      {/* Form content */}
      <Box sx={{ px: 2, py: 1.5 }}>
        {hasForms && formValues.map((form, idx) => (
          <InteractionFormCard
            key={form.id ?? idx}
            form={form}
            service={service}
            onChange={(updatedForm) => {
              if (!updatedForm) return;
              const updated = [...formValues];
              updated[idx] = updatedForm;
              handleFormChange(updated);
            }}
            forms={formValues}
          />
        ))}

        {/* Pending interactions — rendered with form fields */}
        {isPendingActive && pendingFormValues.map((form, idx) => (
          <InteractionFormCard
            key={form.id ?? idx}
            form={form}
            service={service}
            onChange={(updatedForm) => {
              if (!updatedForm) return;
              const updated = pendingFormValues.map((f) => f.id === updatedForm.id ? updatedForm : f);
              setPendingFormValues(updated);
            }}
            forms={pendingFormValues}
          />
        ))}

        {error && (
          <Typography sx={{ fontSize: '12px', color: 'error.main', mt: 1 }}>{error}</Typography>
        )}
      </Box>
    </Box>
  );
};
